// Утилиты для работы с документами
import { getDocumentVariablesData } from './documentVariables';
import { formatLocalDate, formatLocalDateTime } from './dateTime';

// Рендер управляющих конструкций [[if ...]]...[[endif]] в HTML-обёртки (без вычисления условия).
// Нужен для превью/просмотра, чтобы маркеры не были видны пользователю.
export const renderConditionalBlocksToWrappers = (html) => {
  if (!html) return html;
  const src = String(html);
  const lower = src.toLowerCase();
  const closeToken = '[[endif]]';

  const escapeAttr = (v) => String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const findMatchingEndIf = (fromIdx) => {
    let depth = 1;
    let i = fromIdx;
    while (i < src.length) {
      const nextIf = lower.indexOf('[[if', i);
      const nextEnd = lower.indexOf(closeToken, i);
      if (nextEnd === -1) return -1;
      if (nextIf !== -1 && nextIf < nextEnd) {
        const close = src.indexOf(']]', nextIf);
        if (close === -1) return -1;
        depth += 1;
        i = close + 2;
        continue;
      }
      depth -= 1;
      if (depth === 0) return nextEnd;
      i = nextEnd + closeToken.length;
    }
    return -1;
  };

  let out = '';
  let cursor = 0;
  while (true) {
    const ifIdx = lower.indexOf('[[if', cursor);
    if (ifIdx === -1) break;
    const headerEnd = src.indexOf(']]', ifIdx);
    if (headerEnd === -1) break;
    const condRaw = src.slice(ifIdx + 4, headerEnd).trim();
    const afterIf = headerEnd + 2;
    const endIdx = findMatchingEndIf(afterIf);
    if (endIdx === -1) break;

    const body = src.slice(afterIf, endIdx);
    const renderedBody = renderConditionalBlocksToWrappers(body);

    const parts = String(condRaw || '').trim().split('@@');
    const cleanCond = String(parts[0] || '').trim();
    const groupId = String(parts[1] || '').trim();
    const gidAttr = groupId ? ` data-cgid="${escapeAttr(groupId)}"` : '';

    const isBlockBody = /<(p|div|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|blockquote|pre)\b/i.test(renderedBody);
    const wrapped = isBlockBody
      ? `<div class="conditional-section" data-if="${escapeAttr(cleanCond)}"${gidAttr}><div class="conditional-body">${renderedBody}</div></div>`
      : `<span class="conditional-inline" data-if="${escapeAttr(cleanCond)}"${gidAttr}><span class="conditional-body">${renderedBody}</span></span>`;

    out += src.slice(cursor, ifIdx) + wrapped;
    cursor = endIdx + closeToken.length;
  }
  out += src.slice(cursor);
  return out;
};

/**
 * Генерирует автоматическое название документа на основе шаблона и текущей даты
 * @param {Object} template - объект шаблона
 * @param {Object} auction - объект торгов (опционально)
 * @param {Array} existingDocuments - список существующих документов для проверки уникальности
 * @returns {string} - сгенерированное уникальное название документа
 */
export const generateDocumentTitle = (template, auction = null, existingDocuments = []) => {
  const today = new Date();
  const dateStr = today.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  let baseName = template.name || 'Документ';

  // Убираем слово "шаблон" из названия, если оно есть
  baseName = baseName.replace(/\b[Шш]аблон\b\s*/g, '').trim();

  // Базовое название: только имя документа + дата
  let baseTitle = `${baseName} от ${dateStr}`;

  // Проверяем уникальность
  let finalTitle = baseTitle;
  let counter = 1;

  // Получаем список названий существующих документов
  const existingTitles = existingDocuments.map(doc => doc.title || doc.name).filter(Boolean);

  // Если название уже существует, добавляем номер
  while (existingTitles.includes(finalTitle)) {
    counter++;
    finalTitle = `${baseTitle} (${counter})`;
  }

  return finalTitle;
};

/**
 * Подставляет переменные из данных торгов в контент шаблона
 * @param {string} content - контент шаблона с переменными
 * @param {Object} auctionData - данные торгов и связанных объектов
 * @returns {string} - контент с подставленными значениями
 */
export const substituteTemplateVariables = (content, auctionData) => {
  const { auction, debtors, organizers, lots, etps, variablesData } = auctionData;

  const rawLots = Array.isArray(lots) ? lots : (Array.isArray(lots?.results) ? lots.results : []);
  const auctionId = auction?.id != null ? String(auction.id) : '';
  const hasAuctionField = rawLots.some((lot) => lot && (
    lot.auction !== undefined
    || lot.auction_id !== undefined
    || lot.auctionId !== undefined
    || lot?.auction?.id !== undefined
  ));
  const scopedLots = (auctionId && hasAuctionField)
    ? rawLots.filter((lot) => String(lot?.auction || lot?.auction_id || lot?.auctionId || lot?.auction?.id || '') === auctionId)
    : rawLots;

  // Используем нашу функцию генерации переменных с автоматическим склонением
  const variables = variablesData || getDocumentVariablesData(auction, debtors || [], organizers || [], scopedLots || [], etps || []);

  const applyTextTransforms = (value, transforms) => {
    let result = value == null ? '' : String(value);
    const list = Array.isArray(transforms) ? transforms : [];

    const upperFirst = (s) => {
      const m = String(s).match(/^(\s*)([\s\S])([\s\S]*)$/);
      if (!m) return s;
      return m[1] + String(m[2]).toLocaleUpperCase('ru-RU') + (m[3] || '');
    };
    const lowerFirst = (s) => {
      const m = String(s).match(/^(\s*)([\s\S])([\s\S]*)$/);
      if (!m) return s;
      return m[1] + String(m[2]).toLocaleLowerCase('ru-RU') + (m[3] || '');
    };
    const getAuctionTradeFormCode = (raw) => {
      const t = String(raw ?? '').trim().toLocaleLowerCase('ru-RU');
      if (!t) return null;
      if (t === 'open' || t.startsWith('откр')) return 'open';
      if (t === 'closed' || t.startsWith('закр')) return 'closed';
      return null;
    };
    const applyAuctionTradeFormEnding = (raw, variant) => {
      const code = getAuctionTradeFormCode(raw);
      if (!code) return String(raw ?? '');
      const forms = code === 'open'
        ? { i: 'открытые', r: 'открытых', d: 'открытым', v: 'открытые', t: 'открытыми', p: 'открытых' }
        : { i: 'закрытые', r: 'закрытых', d: 'закрытым', v: 'закрытые', t: 'закрытыми', p: 'закрытых' };
      const key = String(variant || 'i').trim();
      return forms[key] || forms.i;
    };
    const stripLeadingFormWord = (s) => String(s ?? '').replace(/^\s*Форма\s+/i, '').trim();
    const toNominativeAuctionFormText = (s) => {
      const raw = stripLeadingFormWord(s);
      const m1 = raw.match(/^(открытого|закрытого)\\s+(аукциона|конкурса)\\s+с\\s+(открытой|закрытой)\\s+формой\\s+подачи\\s+предложения\\s+о\\s+цене$/i);
      if (m1) {
        const adjGen = m1[1].toLowerCase();
        const kindGen = m1[2].toLowerCase();
        const offerAdj = m1[3].toLowerCase();
        const adjNom = adjGen === 'открытого' ? 'открытый' : 'закрытый';
        const kindNom = kindGen === 'аукциона' ? 'аукцион' : 'конкурс';
        return `${adjNom} ${kindNom} с ${offerAdj} формой подачи предложения о цене`;
      }

      const m2 = raw.match(/^(открытых|закрытых)\\s+торгов\\s+(посредством|по\\s+средствам)\\s+публичного\\s+предложения$/i);
      if (m2) {
        const adjGenPl = m2[1].toLowerCase();
        const adjNomPl = adjGenPl === 'открытых' ? 'открытые' : 'закрытые';
        return `${adjNomPl} торги посредством публичного предложения`;
      }

      return raw;
    };

    for (const t of list.map((x) => String(x || '').trim()).filter(Boolean)) {
      if (t === 'upper') result = result.toLocaleUpperCase('ru-RU');
      else if (t === 'lower') result = result.toLocaleLowerCase('ru-RU');
      else if (t === 'upperFirst') result = upperFirst(result);
      else if (t === 'lowerFirst') result = lowerFirst(result);
      else if (t === 'tradePlural') result = applyAuctionTradeFormEnding(result, 'i');
      else if (t === 'tradePlural_r') result = applyAuctionTradeFormEnding(result, 'r');
      else if (t === 'tradePlural_d') result = applyAuctionTradeFormEnding(result, 'd');
      else if (t === 'tradePlural_v') result = applyAuctionTradeFormEnding(result, 'v');
      else if (t === 'tradePlural_t') result = applyAuctionTradeFormEnding(result, 't');
      else if (t === 'tradePlural_p') result = applyAuctionTradeFormEnding(result, 'p');
      else if (t === 'noForm') result = stripLeadingFormWord(result);
      else if (t === 'nom') result = toNominativeAuctionFormText(result);
      else if (t === 'lines') result = String(result).replace(/\r?\n/g, '<br/>');
    }
    return result;
  };

  const ensureParagraphLineHeight = (html, lineHeight) => {
    if (!html || !lineHeight) return html;
    return String(html).replace(/<p([^>]*)>/gi, (match, attrsRaw) => {
      const attrs = String(attrsRaw || '');
      if (/data-line-height\s*=/i.test(attrs)) return match;

      const styleMatch = attrs.match(/\bstyle\s*=\s*(['"])([\s\S]*?)\1/i);
      if (styleMatch) {
        const styleValue = String(styleMatch[2] || '');
        if (/line-height\s*:/i.test(styleValue)) return match;
        const quote = styleMatch[1];
        const nextStyle = `${styleValue.trim().replace(/;?\s*$/, ';')} line-height: ${lineHeight};`;
        return `<p${attrs.replace(styleMatch[0], `style=${quote}${nextStyle}${quote}`)} data-line-height="${lineHeight}">`;
      }

      return `<p${attrs} style="line-height: ${lineHeight};" data-line-height="${lineHeight}">`;
    });
  };

  const findLineHeightMatches = (text) => {
    const source = String(text || '');
    const matches = [];
    const collect = (regex, groupIdx) => {
      let m;
      while ((m = regex.exec(source)) !== null) {
        const n = Number(m[groupIdx]);
        if (Number.isFinite(n) && n >= 0.8 && n <= 3) {
          matches.push({ index: m.index, value: String(Number(n.toFixed(2))) });
        }
      }
    };
    collect(/data-line-height\s*=\s*['"]\s*([0-9]+(?:\.[0-9]+)?)\s*['"]/gi, 1);
    collect(/line-height\s*:\s*([0-9]+(?:\.[0-9]+)?)/gi, 1);
    matches.sort((a, b) => a.index - b.index);
    return matches;
  };

  const detectContextLineHeight = (source, startIdx, endIdx) => {
    const raw = String(source || '');
    if (!raw) return null;
    const all = findLineHeightMatches(raw);
    if (!all.length) return null;
    const before = all.filter((item) => item.index < startIdx);
    if (before.length) return before[before.length - 1].value;
    const after = all.filter((item) => item.index >= endIdx);
    if (after.length) return after[0].value;
    return null;
  };

  const multilineParagraphVariables = new Set(['price_decrease_rules', 'contest_conditions']);

  const applyVariableDisplayAdjustments = (key, value) => {
    if (value == null) return '';
    const text = String(value);
    if (key === 'property_lots_desc_lines') {
      return `<!--AOT_PROPERTY_LOTS_DESC_START-->${text}<!--AOT_PROPERTY_LOTS_DESC_END-->`;
    }
    if (multilineParagraphVariables.has(key)) {
      return `<!--AOT_MULTILINE_VAR_START:${key}-->${text}<!--AOT_MULTILINE_VAR_END:${key}-->`;
    }
    return text;
  };

  // ===== Обработка условных блоков =====
  // ВАЖНО: в готовом документе НЕ должны отображаться бейджи/линии редактора,
  // поэтому здесь работаем только с управляющими конструкциями [[if]]...[[endif]]
  // и в конце вычищаем возможные HTML-обёртки.
  const extractHtmlAttr = (attrs, name) => {
    if (!attrs) return '';
    const m = String(attrs).match(new RegExp(`\\b${name}\\s*=\\s*(['"])([\\s\\S]*?)\\1`, 'i'));
    if (!m) return '';
    return String(m[2] || '')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const hasClass = (attrs, className) => {
    const cls = extractHtmlAttr(attrs, 'class');
    if (!cls) return false;
    return cls.split(/\s+/g).includes(className);
  };

  const serializeConditionBlocks = (html) => {
    if (!html) return html;
    let out = String(html);
    let prev = null;
    const hasConditionalWrapper = (src) =>
      /conditional-(inline|block|section)/i.test(src) && /data-if=/i.test(src);
    const safeParagraphBlockReplace = (tagName, tagAttrs, attrs, body, original) => {
      // Если в body уже есть span-теги, это смешанный абзац с несколькими inline IF.
      // В таком случае не оборачиваем целый абзац в один IF.
      if (/<\/?\s*span\b/i.test(body || '')) return original;
      if (!hasClass(attrs, 'conditional-inline') && !hasClass(attrs, 'conditional-block')) return original;
      const cond = extractHtmlAttr(attrs, 'data-if');
      const gid = extractHtmlAttr(attrs, 'data-cgid');
      if (!String(cond || '').trim()) return original;
      return `[[if ${String(cond || '').trim()}${gid ? `@@${gid}` : ''}]]<${tagName}${tagAttrs}>${body}</${tagName}>[[endif]]`;
    };

    while (prev !== out) {
      prev = out;
      out = String(out)
      // Inline условие, оборачивающее весь абзац/элемент списка
      .replace(/<p([^>]*)>\s*<span([^>]*)>\s*<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>\s*<\/span>\s*<\/p>/gi,
        (m, pAttrs, spanAttrs, body) => safeParagraphBlockReplace('p', pAttrs, spanAttrs, body, m))
      .replace(/<li([^>]*)>\s*<span([^>]*)>\s*<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>\s*<\/span>\s*<\/li>/gi,
        (m, liAttrs, spanAttrs, body) => safeParagraphBlockReplace('li', liAttrs, spanAttrs, body, m))
      // Блочная версия (новая и старая) — порядок атрибутов может быть любым
      .replace(/<div([^>]*)>\s*<div[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
        (m, attrs, body) => {
          if (!hasClass(attrs, 'conditional-section') && !hasClass(attrs, 'conditional-block')) return m;
          const cond = extractHtmlAttr(attrs, 'data-if');
          const gid = extractHtmlAttr(attrs, 'data-cgid');
          return `[[if ${String(cond || '').trim()}${gid ? `@@${gid}` : ''}]]${body}[[endif]]`;
        })
      // Блочная версия без conditional-body (некоторые шаблоны сохраняются именно так)
      .replace(/<div([^>]*)>([\s\S]*?)<\/div>/gi,
        (m, attrs, body) => {
          if (!hasClass(attrs, 'conditional-section') && !hasClass(attrs, 'conditional-block')) return m;
          const cond = extractHtmlAttr(attrs, 'data-if');
          const gid = extractHtmlAttr(attrs, 'data-cgid');
          return `[[if ${String(cond || '').trim()}${gid ? `@@${gid}` : ''}]]${body}[[endif]]`;
        })
      // Inline версия (новая и старая) — порядок атрибутов может быть любым
      .replace(/<span([^>]*)>\s*<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>\s*<\/span>/gi,
        (m, attrs, body) => {
          const cond = extractHtmlAttr(attrs, 'data-if');
          const gid = extractHtmlAttr(attrs, 'data-cgid');
          if (!cond && !hasClass(attrs, 'conditional-inline') && !hasClass(attrs, 'conditional-block')) return m;
          return `[[if ${String(cond || '').trim()}${gid ? `@@${gid}` : ''}]]${body}[[endif]]`;
        })
      // Старый вариант с badge
      .replace(/<span([^>]*)>\s*<span[^>]*class="[^"]*conditional-badge[^"]*"[^>]*>[\s\S]*?<\/span>\s*<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>\s*<\/span>/gi,
        (m, attrs, body) => {
          if (!hasClass(attrs, 'conditional-block')) return m;
          const cond = extractHtmlAttr(attrs, 'data-if');
          return `[[if ${String(cond || '').trim()}]]${body}[[endif]]`;
        });
      if (!hasConditionalWrapper(out)) break;
    }
    return out;
  };

  const serializeLoopBlocks = (html) => {
    if (!html) return html;
    return String(html).replace(
      /<div([^>]*)>\s*(?:<div[^>]*class="[^"]*\bloop-header\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?<div[^>]*class="[^"]*\bloop-body\b[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
      (m, attrs, body) => {
        if (!hasClass(attrs, 'loop-section')) return m;
        const loopExpr = extractHtmlAttr(attrs, 'data-for');
        if (!loopExpr) return m;
        return `[[for ${String(loopExpr || '').trim()}]]${body}[[endfor]]`;
      }
    );
  };

  const stripConditionalWrappers = (html) => {
    if (!html) return html;
    return String(html)
      .replace(/<div([^>]*)>\s*<div[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
        (m, attrs, body) => (hasClass(attrs, 'conditional-section') || hasClass(attrs, 'conditional-block')) ? body : m)
      .replace(/<span([^>]*)>\s*<span[^>]*class="[^"]*conditional-body[^"]*"[^>]*>([\s\S]*?)<\/span>\s*<\/span>/gi,
        (m, attrs, body) => (hasClass(attrs, 'conditional-inline') || hasClass(attrs, 'conditional-block')) ? body : m)
      .replace(/<span[^>]*class="[^"]*conditional-badge[^"]*"[^>]*>[\s\S]*?<\/span>/gi, '');
  };

  // Проверка условия: поддерживает &&, ||, ==, !=, empty/not_empty, contains, сравнение дат
  const evaluateCondition = (expression, vars) => {
    if (!expression) return false;

    const normalizeLegacyExpression = (exp) => {
      // Совместимость: ранее в некоторых условиях мог ошибочно сохраниться литерал 'legal'
      // для переменных, где ожидаются другие значения (из конструктора условий).
      // Нормализуем только сравнение на равенство, чтобы не менять семантику "не равно".
      return String(exp || '')
        .replace(/\bdebtor_manager_act_basis_code\b\s*==\s*(['"])legal\1/gi, "debtor_manager_act_basis_code == 'same'")
        .replace(/\bauction_conductor_code\b\s*==\s*(['"])legal\1/gi, "auction_conductor_code == 'ot'");
    };

    expression = normalizeLegacyExpression(expression);
    // На всякий случай: если условие пришло в HTML-виде
    expression = String(expression || '')
      .replace(/&amp;&amp;/g, '&&')
      .replace(/&#38;&#38;/g, '&&')
      .replace(/&amp;#38;&amp;#38;/g, '&&')
      .replace(/&amp;/g, '&');

    // ВАЖНО: старый разбор через split по ||/&& игнорировал скобки и приоритеты.
    // Это приводило к ложноположительным срабатываниям условий вида:
    //   A && (B || C || D)
    // Вместо этого используем простой парсер с поддержкой скобок и приоритетов: && сильнее ||.

    const trim = (v) => (typeof v === 'string' ? v.trim() : v);
    const getVal = (name) => trim(vars?.[name]);

    const parseDate = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };

    const tokenize = (src) => {
      const s = String(src || '');
      const tokens = [];
      let i = 0;
      const push = (type, value) => tokens.push({ type, value });
      const isSpace = (c) => /\s/.test(c);
      const isIdentStart = (c) => /[A-Za-z_]/.test(c);
      const isIdent = (c) => /[A-Za-z0-9_.]/.test(c);

      while (i < s.length) {
        const ch = s[i];
        if (isSpace(ch)) { i++; continue; }
        if (ch === '(') { push('LPAREN', ch); i++; continue; }
        if (ch === ')') { push('RPAREN', ch); i++; continue; }
        if (ch === ',') { push('COMMA', ch); i++; continue; }
        if (s.slice(i, i + 2) === '&&') { push('AND', '&&'); i += 2; continue; }
        if (s.slice(i, i + 2) === '||') { push('OR', '||'); i += 2; continue; }
        if (s.slice(i, i + 2) === '==') { push('EQ', '=='); i += 2; continue; }
        if (s.slice(i, i + 2) === '!=') { push('NEQ', '!='); i += 2; continue; }
        if (ch === '"' || ch === "'") {
          const quote = ch;
          i++;
          let out = '';
          while (i < s.length) {
            const c = s[i];
            if (c === '\\' && i + 1 < s.length) {
              out += s[i + 1];
              i += 2;
              continue;
            }
            if (c === quote) { i++; break; }
            out += c;
            i++;
          }
          push('STRING', out);
          continue;
        }
        if (isIdentStart(ch)) {
          let start = i;
          i++;
          while (i < s.length && isIdent(s[i])) i++;
          push('IDENT', s.slice(start, i));
          continue;
        }
        // Неизвестный символ — прекращаем разбор.
        throw new Error(`Неизвестный символ в условии: ${ch}`);
      }
      return tokens;
    };

    const tokens = tokenize(expression);
    let pos = 0;
    const peek = () => tokens[pos] || null;
    const take = (type) => {
      const t = tokens[pos];
      if (!t || (type && t.type !== type)) return null;
      pos++;
      return t;
    };
    const expect = (type) => {
      const t = take(type);
      if (!t) throw new Error(`Ожидался токен ${type}`);
      return t;
    };

    const evalPredicate = () => {
      const ident = expect('IDENT').value;
      // Функции
      if (String(ident).toLowerCase() === 'empty') {
        expect('LPAREN');
        const v = expect('IDENT').value;
        expect('RPAREN');
        const val = getVal(v);
        return val === undefined || val === null || val === '';
      }
      if (String(ident).toLowerCase() === 'not_empty') {
        expect('LPAREN');
        const v = expect('IDENT').value;
        expect('RPAREN');
        const val = getVal(v);
        return val !== undefined && val !== null && val !== '';
      }
      if (String(ident).toLowerCase() === 'contains') {
        expect('LPAREN');
        const v = expect('IDENT').value;
        expect('COMMA');
        const subTok = take('STRING') || take('IDENT');
        if (!subTok) throw new Error('Ожидалась строка/значение для contains');
        expect('RPAREN');
        const val = String(getVal(v) || '').toLowerCase();
        const sub = String(subTok.value || '').toLowerCase();
        return val.includes(sub);
      }

      // Сравнения
      const opTok = take('EQ') || take('NEQ');
      if (!opTok) throw new Error('Ожидался оператор сравнения ==/!=');
      const rightTok = take('STRING') || take('IDENT');
      if (!rightTok) throw new Error('Ожидалось значение справа от сравнения');

      const left = getVal(ident);
      const rightRaw = String(rightTok.value || '');
      const right = trim(vars?.[rightRaw]) ?? rightRaw;

      const leftDate = parseDate(left);
      const rightDate = parseDate(right);
      if (leftDate !== null && rightDate !== null) {
        return opTok.type === 'EQ' ? leftDate === rightDate : leftDate !== rightDate;
      }
      return opTok.type === 'EQ' ? String(left) === String(right) : String(left) !== String(right);
    };

    const parsePrimary = () => {
      if (take('LPAREN')) {
        const v = parseOr();
        expect('RPAREN');
        return v;
      }
      return evalPredicate();
    };

    const parseAnd = () => {
      let left = parsePrimary();
      while (take('AND')) {
        const right = parsePrimary();
        left = left && right;
      }
      return left;
    };

    const parseOr = () => {
      let left = parseAnd();
      while (take('OR')) {
        const right = parseAnd();
        left = left || right;
      }
      return left;
    };

    try {
      const result = parseOr();
      // если остались токены — считаем выражение некорректным
      if (peek()) return false;
      return !!result;
    } catch (e) {
      // Если условие некорректно — считаем его ложным (безопаснее, чем показывать лишний текст).
      return false;
    }
  };

  const resolveConditionalBlocks = (text, vars) => {
    if (!text) return text;
    // Поддерживаем пустое условие: в этом случае блок не должен показываться.
    const ifRegex = /\[\[if\s*([^\]]*)\]\]/gi;
    let result = '';
    let cursor = 0;
    const removedMarker = '<!--AOT_IF_REMOVED-->';

    const findMatchingEndIf = (src, fromIdx) => {
      // Ищем matching [[endif]] с поддержкой вложенных [[if]]...[[endif]].
      // Возвращает индекс начала matching [[endif]] или -1.
      let depth = 1;
      let i = fromIdx;
      while (i < src.length) {
        const nextIf = src.toLowerCase().indexOf('[[if', i);
        const nextEnd = src.toLowerCase().indexOf('[[endif]]', i);
        if (nextEnd === -1) return -1;
        if (nextIf !== -1 && nextIf < nextEnd) {
          // Находим закрывающую скобку ']]' для этого if; если её нет — считаем синтаксис битым.
          const close = src.indexOf(']]', nextIf);
          if (close === -1) return -1;
          depth += 1;
          i = close + 2;
          continue;
        }
        // nextEnd раньше любого nextIf
        depth -= 1;
        if (depth === 0) return nextEnd;
        i = nextEnd + '[[endif]]'.length;
      }
      return -1;
    };

    const skipTrailingLineBreak = () => {
      // Если условный блок скрыт и сразу после него стоит перевод строки (<br>),
      // то его тоже скрываем, чтобы не оставалась пустая строка.
      const rest = text.slice(cursor);
      const m = rest.match(/^\s*<br\s*\/?>/i);
      if (m) {
        cursor += m[0].length;
        ifRegex.lastIndex = cursor;
      }
    };

    while (true) {
      const match = ifRegex.exec(text);
      if (!match) break;
      const start = match.index;
      const condition = match[1].split('@@')[0].trim();
      const afterIf = ifRegex.lastIndex;
      const endIdx = findMatchingEndIf(text, afterIf);
      if (endIdx === -1) break;
      const blockStart = afterIf;
      const blockEnd = endIdx;

      result += text.slice(cursor, start);
      const blockContent = text.slice(blockStart, blockEnd);
      const isTrue = evaluateCondition(condition, vars);
      if (isTrue) {
        result += resolveConditionalBlocks(blockContent, vars);
      } else {
        // Оставляем маркер: потом удалим только "пустую строку", которая образовалась из-за скрытого блока,
        // не затрагивая пользовательские пустые строки/абзацы в других местах.
        result += removedMarker;
      }
      cursor = endIdx + '[[endif]]'.length;
      ifRegex.lastIndex = cursor;

      if (!isTrue) {
        skipTrailingLineBreak();
      }
    }
    result += text.slice(cursor);
    return result;
  };

  const resolveVarPath = (vars, path) => {
    if (!path) return undefined;
    const parts = String(path).split('.');
    let cur = vars;
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  const resolveForBlocks = (text, vars) => {
    if (!text) return text;
    const src = String(text);
    const lower = src.toLowerCase();
    const openToken = '[[for';
    const closeToken = '[[endfor]]';

    const findMatchingEndFor = (fromIdx) => {
      let depth = 1;
      let i = fromIdx;
      while (i < src.length) {
        const nextFor = lower.indexOf(openToken, i);
        const nextEnd = lower.indexOf(closeToken, i);
        if (nextEnd === -1) return -1;
        if (nextFor !== -1 && nextFor < nextEnd) {
          const close = src.indexOf(']]', nextFor);
          if (close === -1) return -1;
          depth += 1;
          i = close + 2;
          continue;
        }
        depth -= 1;
        if (depth === 0) return nextEnd;
        i = nextEnd + closeToken.length;
      }
      return -1;
    };

    let out = '';
    let cursor = 0;
    while (true) {
      const forIdx = lower.indexOf(openToken, cursor);
      if (forIdx === -1) break;
      const headerEnd = src.indexOf(']]', forIdx);
      if (headerEnd === -1) break;
      const header = src.slice(forIdx + openToken.length, headerEnd).trim();
      const afterFor = headerEnd + 2;
      const endIdx = findMatchingEndFor(afterFor);
      if (endIdx === -1) break;
      const loopLineHeight = detectContextLineHeight(src, forIdx, endIdx + closeToken.length);

      out += src.slice(cursor, forIdx);
      const block = src.slice(afterFor, endIdx);

      const m = header.match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s+in\s+([A-Za-z0-9_.]+)\s*$/i);
      const loopVar = m ? m[1] : '';
      const listName = m ? m[2] : '';
      const listValue = resolveVarPath(vars || {}, listName);
      const items = Array.isArray(listValue) ? listValue : [];

      const rendered = items.map((item) => {
        const localVars = { ...(vars || {}), [loopVar]: item };
        let chunk = resolveForBlocks(block, localVars);
        chunk = resolveConditionalBlocks(chunk, localVars);
        chunk = replaceVariablesWithModifiersWithVars(chunk, localVars);
        chunk = ensureParagraphLineHeight(chunk, loopLineHeight);
        return chunk;
      }).join('');

      out += rendered;
      cursor = endIdx + closeToken.length;
    }
    out += src.slice(cursor);
    return out;
  };

  const replaceVariablesWithModifiersWithVars = (html, vars) => {
    if (!html) return html;
    return String(html).replace(/\{\{\s*([^}]+?)\s*\}\}/g, (m, inner) => {
      const raw = String(inner || '').trim();
      if (!raw) return m;
      const parts = raw.split('|').map((p) => p.trim()).filter(Boolean);
      if (parts.length === 0) return m;
      const key = parts[0];
      const transforms = parts.slice(1);
      const value = resolveVarPath(vars || {}, key);
      if (value === undefined) return m;
      if (!transforms.length) return applyVariableDisplayAdjustments(key, value);
      return applyVariableDisplayAdjustments(key, applyTextTransforms(value, transforms));
    });
  };

  // Сначала приводим любые HTML-обёртки условных блоков к [[if]]...[[endif]]
  let processedContent = serializeLoopBlocks(content);
  processedContent = serializeConditionBlocks(processedContent);

  // Сначала обрабатываем согласование по роду, пока плейсхолдеры {{...}} ещё на месте
  try {
    const { resolveGenderAgreement } = require('./genderAgreement');
    processedContent = resolveGenderAgreement(processedContent, variables);
  } catch (error) {
    console.warn('Gender agreement processing failed:', error);
  }

  // Перед подстановкой переменных — убираем условные блоки с ложными условиями
  processedContent = resolveForBlocks(processedContent, variables);
  processedContent = resolveConditionalBlocks(processedContent, variables);

  // На всякий случай убираем управляющие конструкции (если они остались из-за некорректного синтаксиса),
  // чтобы в итоговом документе не было "машинного" кода.
  processedContent = processedContent
    .replace(/\[\[if\s+[^\]]*\]\]/gi, '')
    .replace(/\[\[endif\]\]/gi, '');
  processedContent = processedContent
    .replace(/\[\[for\s+[^\]]*\]\]/gi, '')
    .replace(/\[\[endfor\]\]/gi, '');

  // Заменяем переменные в формате {{variable_name}} и {{variable_name|lowerFirst}} и т.п.
  processedContent = replaceVariablesWithModifiersWithVars(processedContent, variables);

  // Спец-обработка описания лотов: если переменная стояла отдельным абзацем и содержит <br>,
  // разворачиваем её в набор обычных <p> с теми же атрибутами абзаца.
  processedContent = String(processedContent).replace(
    /<p([^>]*)>\s*<!--AOT_PROPERTY_LOTS_DESC_START-->([\s\S]*?)<!--AOT_PROPERTY_LOTS_DESC_END-->\s*<\/p>/gi,
    (full, attrs, inner) => {
      const parts = String(inner || '')
        .split(/<br\s*\/?>/i)
        .map((chunk) => String(chunk || '').trim())
        .filter(Boolean);
      if (!parts.length) return `<p${attrs}></p>`;
      return parts.map((chunk) => `<p${attrs}>${chunk}</p>`).join('');
    }
  ).replace(/<!--AOT_PROPERTY_LOTS_DESC_START-->|<!--AOT_PROPERTY_LOTS_DESC_END-->/g, '');

  // Убираем любые HTML-обёртки условных блоков и бейджи (в готовом документе их быть не должно)
  processedContent = stripConditionalWrappers(processedContent);
  processedContent = String(processedContent).replace(
    /<div[^>]*class="[^"]*\bloop-section\b[^"]*"[^>]*>\s*(?:<div[^>]*class="[^"]*\bloop-header\b[^"]*"[^>]*>[\s\S]*?<\/div>\s*)?<div[^>]*class="[^"]*\bloop-body\b[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi,
    (m, body) => body
  );

  // Эти переменные обычно стоят внутри форматированного абзаца после вводной фразы.
  // Разворачиваем многострочное значение в такие же <p>, чтобы Tiptap не потерял стиль абзаца.
  processedContent = String(processedContent).replace(
    /<p([^>]*)>([\s\S]*?)<\/p>/gi,
    (full, attrs, inner) => {
      const markerMatch = String(inner || '').match(/<!--AOT_MULTILINE_VAR_START:([a-z0-9_]+)-->/i);
      if (!markerMatch) return full;
      const key = markerMatch[1];
      const startMarker = `<!--AOT_MULTILINE_VAR_START:${key}-->`;
      const endMarker = `<!--AOT_MULTILINE_VAR_END:${key}-->`;
      const startIdx = String(inner || '').indexOf(startMarker);
      if (startIdx === -1) return full;
      const endIdx = String(inner || '').indexOf(endMarker, startIdx + startMarker.length);
      if (endIdx === -1) return full;

      const before = inner.slice(0, startIdx);
      const rawValue = inner.slice(startIdx + startMarker.length, endIdx);
      const after = inner.slice(endIdx + endMarker.length);
      const lines = String(rawValue || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map((line) => String(line || '').trim())
        .filter(Boolean);

      if (!lines.length) return `<p${attrs}>${before}${after}</p>`;
      if (lines.length === 1) return `<p${attrs}>${before}${lines[0]}${after}</p>`;

      return lines.map((line, index) => {
        const prefix = index === 0 ? before : '';
        const suffix = index === lines.length - 1 ? after : '';
        return `<p${attrs}>${prefix}${line}${suffix}</p>`;
      }).join('');
    }
  );
  processedContent = String(processedContent).replace(
    /<!--AOT_MULTILINE_VAR_START:[a-z0-9_]+-->|<!--AOT_MULTILINE_VAR_END:[a-z0-9_]+-->/gi,
    ''
  );

  // Убираем пустые строки/абзацы ТОЛЬКО там, где они образовались из-за скрытого IF-блока.
  // Пользовательские пустые строки должны сохраняться.
  processedContent = String(processedContent)
    // IF внутри inline-контекста часто оставляет двойной <br>:
    // "...<br><!--AOT_IF_REMOVED--><br>...". Схлопываем только такие случаи.
    .replace(
      /(<br\b[^>]*>)\s*(?:(?:&nbsp;|\u00A0|\s)*<!--AOT_IF_REMOVED-->\s*)+(<br\b[^>]*>)/gi,
      '$1'
    )
    .replace(
      /<p\b[^>]*>\s*(?:(?:&nbsp;|\u00A0|\s|<br\s*\/?>)*<!--AOT_IF_REMOVED-->)+(?:(?:&nbsp;|\u00A0|\s|<br\s*\/?>)*)<\/p>/gi,
      ''
    )
    .replace(
      /<div\b[^>]*>\s*(?:(?:&nbsp;|\u00A0|\s|<br\s*\/?>)*<!--AOT_IF_REMOVED-->)+(?:(?:&nbsp;|\u00A0|\s|<br\s*\/?>)*)<\/div>/gi,
      ''
    )
    // Маркер мог остаться внутри строки/абзаца (если IF был inline) — просто убираем.
    .replace(/<!--AOT_IF_REMOVED-->/g, '');

  // Удаляем "технические" пустые абзацы-артефакты после IF/FOR:
  // <p><span ...></span></p>, <p><strong></strong></p> и т.п.
  // Важно: не трогаем действительно пустой <p></p>, который пользователь мог создать вручную.
  const INLINE_EMPTY_TAGS = new Set(['span', 'strong', 'b', 'em', 'i', 'u', 'mark', 'br']);
  const isArtifactEmptyParagraphInner = (innerHtml) => {
    const src = String(innerHtml || '');
    // Универсально чистим абзацы, состоящие только из "висячих" закрывающих inline-тегов
    // (появляются после удаления IF-обёрток).
    if (/^\s*(?:<\/(?:span|strong|b|em|i|u|mark)\s*>\s*)+$/i.test(src)) return true;

    // Удаляем только технические пустые абзацы, оставшиеся после IF/loop обёрток.
    // Намеренные пустые строки пользователя (обычные <p><span>&nbsp;</span></p>) сохраняем.
    const hasTemplateMarkers = /(?:\bconditional-(?:body|badge|inline|block|section)\b|data-cond-body\b|data-if=|data-cgid=|\bloop-(?:section|body|header)\b|data-for=)/i.test(src);
    if (!hasTemplateMarkers) return false;

    const tags = [...src.matchAll(/<\/?([a-z0-9:-]+)\b[^>]*>/gi)].map((m) => String(m[1] || '').toLowerCase());
    if (!tags.length) return false;
    if (!tags.every((t) => INLINE_EMPTY_TAGS.has(t))) return false;

    const textOnly = src
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<\/?[^>]+>/g, '')
      .replace(/&nbsp;|&#160;|&#xA0;/gi, '')
      .replace(/[\s\u00A0\u200B\uFEFF]+/g, '');
    return textOnly === '';
  };

  processedContent = String(processedContent).replace(
    /<p\b([^>]*)>([\s\S]*?)<\/p>/gi,
    (full, attrs, inner) => (isArtifactEmptyParagraphInner(inner) ? '' : full)
  );

  // Для ячеек таблиц: если IF-блок схлопнулся, редактор часто оставляет
  // один пустой <p> как артефакт. Удаляем пустые абзацы внутри td/th,
  // когда в ячейке уже есть другой (непустой) контент.
  if (typeof document !== 'undefined') {
    try {
      const root = document.createElement('div');
      root.innerHTML = String(processedContent || '');
      const isEmptyParagraph = (el) => {
        if (!el || el.tagName !== 'P') return false;
        if (isArtifactEmptyParagraphInner(el.innerHTML || '')) return true;
        const text = String(el.textContent || '')
          .replace(/\u00A0/g, '')
          .replace(/\s+/g, '');
        const html = String(el.innerHTML || '')
          .replace(/<br\s*\/?>/gi, '')
          .replace(/&nbsp;|&#160;|&#xA0;/gi, '')
          .replace(/\s+/g, '');
        return text === '' && html === '';
      };

      root.querySelectorAll('td, th').forEach((cell) => {
        const paragraphs = Array.from(cell.children).filter((node) => node.tagName === 'P');
        if (!paragraphs.length) return;
        const hasNonEmptyParagraph = paragraphs.some((p) => !isEmptyParagraph(p));
        const hasMeaningfulNonParagraphContent = Array.from(cell.childNodes).some((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return String(node.textContent || '')
              .replace(/\u00A0/g, '')
              .replace(/\s+/g, '') !== '';
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          if (node.tagName === 'P') return false;
          const text = String(node.textContent || '')
            .replace(/\u00A0/g, '')
            .replace(/\s+/g, '');
          const html = String(node.innerHTML || '')
            .replace(/<br\s*\/?>/gi, '')
            .replace(/&nbsp;|&#160;|&#xA0;/gi, '')
            .replace(/\s+/g, '');
          return text !== '' || html !== '';
        });
        if (!hasNonEmptyParagraph && !hasMeaningfulNonParagraphContent) return;
        paragraphs.forEach((p) => {
          if (isEmptyParagraph(p)) p.remove();
        });
      });

      processedContent = root.innerHTML;
    } catch (_) { /* no-op */ }
  }

  return processedContent;
};

/**
 * Форматирует дату для отображения
 * @param {Date|string} date - дата
 * @returns {string} - отформатированная дата
 */

export const formatDate = (date) => {
  return formatLocalDate(date);
};

/**
 * Форматирует дату и время для отображения
 * @param {Date|string} date - дата
 * @returns {string} - отформатированная дата и время
 */
export const formatDateTime = (date) => {
  return formatLocalDateTime(date);
};
